import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { UserAssumptionsDto } from "../../users";
import type { AuthUser, RegionCode } from "../../../types/auth-types/AuthUser";
import {
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
} from "../../../config/auth-storage-keys";
import { authStorage } from "../../../config/session-storage";

const REGION_STORAGE_KEY = "PennRegion";
const VALID_REGIONS: ReadonlySet<RegionCode> = new Set<RegionCode>([
  "EU",
  "NA",
  "ASIA",
]);

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  assumptions: UserAssumptionsDto | null;
  selectedRegion: RegionCode | null;
  /**
   * True until the boot refresh has resolved (ADR-0036). The access token lives
   * in memory, so a fresh page load genuinely does not yet know whether it has
   * a session — `ProtectedRoute` must wait rather than redirect on first paint.
   */
  restoring: boolean;
}

function readStoredUser(): AuthUser | null {
  const raw = authStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    authStorage.removeItem(USER_STORAGE_KEY);
    return null;
  }
}

function readStoredRegion(): RegionCode | null {
  const raw = authStorage.getItem(REGION_STORAGE_KEY);
  if (raw && VALID_REGIONS.has(raw as RegionCode)) return raw as RegionCode;
  return null;
}

function userRegion(user: AuthUser | null): RegionCode | null {
  const code = user?.region?.code;
  return code && VALID_REGIONS.has(code) ? code : null;
}

// Auth is NOT derived at module load any more (ADR-0036). It used to be, and
// that was the first failure: a new tab has no in-memory token, so the old
// module-load read computed `isAuthenticated: false` and the route guard
// redirected before anything could restore the session. The boot refresh in
// `App.tsx` resolves first and dispatches `setLogin` or `setRestored`.
//
// The cached user is still read so the NavBar and the `x-auth-scope` header
// have something to work with during the restore, but it does NOT by itself
// make the tab authenticated — only a real token does.
const cachedUser = readStoredUser();

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  assumptions: null,
  selectedRegion: readStoredRegion() ?? userRegion(cachedUser),
  restoring: true,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLogin: (
      state,
      action: PayloadAction<{
        accessToken: string;
        user: AuthUser;
        assumptions?: UserAssumptionsDto | null;
      }>,
    ) => {
      state.isAuthenticated = true;
      state.restoring = false;
      state.user = action.payload.user;
      state.assumptions = action.payload.assumptions ?? null;
      const region = userRegion(action.payload.user);
      state.selectedRegion = region;
      authStorage.setItem(TOKEN_STORAGE_KEY, action.payload.accessToken);
      authStorage.setItem(USER_STORAGE_KEY, JSON.stringify(action.payload.user));
      if (region) authStorage.setItem(REGION_STORAGE_KEY, region);
      else authStorage.removeItem(REGION_STORAGE_KEY);
    },
    /**
     * The boot refresh finished with no session. Distinct from `setLogout`: it
     * clears nothing (there is nothing to clear) and must not broadcast, it
     * only ends the restoring state so the guards can act.
     */
    setRestored: (state) => {
      state.restoring = false;
    },
    setAssumptions: (state, action: PayloadAction<UserAssumptionsDto | null>) => {
      state.assumptions = action.payload;
    },
    setLogout: (state) => {
      state.isAuthenticated = false;
      state.restoring = false;
      state.user = null;
      state.assumptions = null;
      state.selectedRegion = null;
      authStorage.removeItem(TOKEN_STORAGE_KEY);
      authStorage.removeItem(USER_STORAGE_KEY);
      authStorage.removeItem(REGION_STORAGE_KEY);
      authStorage.removeItem("PennWorkFor");
    },
    setSelectedRegion: (state, action: PayloadAction<RegionCode>) => {
      if (!VALID_REGIONS.has(action.payload)) return;
      const canSwitch = !!state.user?.role?.canSwitchRegion;
      if (!canSwitch) return;
      state.selectedRegion = action.payload;
      authStorage.setItem(REGION_STORAGE_KEY, action.payload);
    },
    /** Replace the cached user (e.g. after a self-service profile edit) without
     *  touching the auth token. Used by the admin console's ProfilePage. */
    setUser: (state, action: PayloadAction<AuthUser>) => {
      state.user = action.payload;
      authStorage.setItem(USER_STORAGE_KEY, JSON.stringify(action.payload));
    },
  },
});

export const {
  setLogin,
  setLogout,
  setRestored,
  setUser,
  setSelectedRegion,
  setAssumptions,
} = authSlice.actions;

export default authSlice.reducer;
